import {Hash} from "../../../interfaces/hashInterfaces";
import {IRoleData, IRoleMetadata} from "../../../interfaces/roleInterfaces";

export enum OrganizationRoleCapability {
  manageMembers = 1,
  manageRoles = 2,
  createGroups = 3,
  manageAllGroups = 4,
  viewAllMemberProfiles = 5,
  manageConnections = 6,
  manageSettings = 7,
  viewAllDocumentsOnSubmissionsDashboard = 8,
  viewAllSubmissionDetailsAndExport = 9,
  assignGroupMembersToWorkflowSteps = 10,
  terminateSubmissionWorkflowSteps = 11,
  viewAllLicenseHistories = 12,
  viewLicenseeHistory = 13,
  viewComplaints = 14,
  manageAllLicenseStatuses = 15,
  createDocumentsAndFolders = 16,
  editAllDocumentsAndFolders = 17,
  moveArchiveAllDocumentsAndFolders = 18,
  publishUnpublishAllOrganizationalDocuments = 19,
  manageAllDocumentAndFolderSharesAndPermissions = 20,
  createPortals = 21,
  editAllPortals = 22,
  deleteAllPortals = 23,
  addRemoveDocumentsInAllPortals = 24,
  publishUnpublishAllPortals = 25,
  manageAllPortalSharesAndPermissions = 26,
  configureAPI = 27,
  inputExternalSubmissionData = 28,
  reassignCompletersForWorkflowSteps = 29,
  manageTemplates = 30,
  manageProfiles = 31,
  viewAllSubmissionDetails = 32,
  manageObjectModels = 33,
  manageEntities = 34,
  manageTrustedOrganizationsAndViewableDocuments = 35,
  createEditArchiveAnalytics = 36,
  createEditArchiveDashboards = 37,
  manageDebug = 38,
  editAllSubmissionDetails = 39,
  deleteSubmissions = 40,
  manageUsers = 41,
  managePeerOrganizations = 42,
  manageQuickLinksFolders = 43,
  createQuickLinksFolders = 44,
  createApps = 45,
  downloadApps = 46,
  uploadApps = 47,
  manageDataTags = 48,
  manageDataTagReports = 49,
  manageAppGroupsAndPermissions = 50,
  transferAppGroupOwner = 51,
  transferAppOwner = 52,
  shareConnections = 53,
}

export const OrganizationRoleMetadata: { [key in OrganizationRoleCapability]: IRoleMetadata } = {
  [OrganizationRoleCapability.manageMembers]: {
    label: 'Manage organization members'
  },
  [OrganizationRoleCapability.manageRoles]: {
    label: 'Manage roles'
  },
  [OrganizationRoleCapability.createGroups]: {
    label: 'Create groups'
  },
  [OrganizationRoleCapability.manageAllGroups]: {
    label: 'Manage all groups'
  },
  [OrganizationRoleCapability.viewAllMemberProfiles]: {
    label: 'View all member profiles'
  },
  [OrganizationRoleCapability.manageConnections]: {
    label: 'Manage connections',
  },
  [OrganizationRoleCapability.manageSettings]: {
    label: 'Manage settings',
  },
  [OrganizationRoleCapability.createDocumentsAndFolders]: {
    label: 'Create documents & folders',
  },
  [OrganizationRoleCapability.editAllDocumentsAndFolders]: {
    label: 'Edit all documents, folders & assign tags',
  },
  [OrganizationRoleCapability.moveArchiveAllDocumentsAndFolders]: {
    label: 'Move/archive all documents & folders',
  },
  [OrganizationRoleCapability.publishUnpublishAllOrganizationalDocuments]: {
    label: 'Publish/unpublish all organizational documents',
  },
  [OrganizationRoleCapability.manageAllDocumentAndFolderSharesAndPermissions]: {
    label: 'Manage all document & folders shares and permissions',
  },
  [OrganizationRoleCapability.viewAllDocumentsOnSubmissionsDashboard]: {
    label: 'View all documents on Reporting & Submissions dashboard'
  },
  [OrganizationRoleCapability.viewAllSubmissionDetailsAndExport]: {
    label: 'View all submission details & export data',
  },
  [OrganizationRoleCapability.assignGroupMembersToWorkflowSteps]: {
    label: 'Assign group completers for any submission',
  },
  [OrganizationRoleCapability.terminateSubmissionWorkflowSteps]: {
    label: 'Terminate submission workflow steps',
  },
  [OrganizationRoleCapability.viewAllLicenseHistories]: {
    label: 'View all license histories',
  },
  [OrganizationRoleCapability.viewLicenseeHistory]: {
    label: 'View licensee history',
  },
  [OrganizationRoleCapability.viewComplaints]: {
    label: 'View complaints',
  },
  [OrganizationRoleCapability.manageAllLicenseStatuses]: {
    label: 'Manage all license statuses',
  },
  [OrganizationRoleCapability.createPortals]: {
    label: 'Create portals',
  },
  [OrganizationRoleCapability.editAllPortals]: {
    label: 'Edit all portals',
  },
  [OrganizationRoleCapability.deleteAllPortals]: {
    label: 'Delete all portals',
  },
  [OrganizationRoleCapability.addRemoveDocumentsInAllPortals]: {
    label: 'Add/Remove documents to all portals',
  },
  [OrganizationRoleCapability.publishUnpublishAllPortals]: {
    label: 'Publish/Unpublish all portals',
  },
  [OrganizationRoleCapability.manageAllPortalSharesAndPermissions]: {
    label: 'Manage all portal shares & permissions',
  },
  [OrganizationRoleCapability.configureAPI]: {
    label: 'Configure API',
  },
  [OrganizationRoleCapability.inputExternalSubmissionData]: {
    label: 'Input External Data',
  },
  [OrganizationRoleCapability.reassignCompletersForWorkflowSteps]: {
    label: 'Reassign completer assignments for any submission',
  },
  [OrganizationRoleCapability.manageTemplates]: {
    label: 'Create, edit & archive all templates',
  },
  [OrganizationRoleCapability.manageProfiles]: {
    label: 'Create profiles for prepopulating fields across forms',
  },
  [OrganizationRoleCapability.viewAllSubmissionDetails]: {
    label: 'View all submission details',
  },
  [OrganizationRoleCapability.manageObjectModels]: {
    label: 'Create models, views & mappings',
  },
  [OrganizationRoleCapability.manageEntities]: {
    label: "Create and manage entity types"
  },
  [OrganizationRoleCapability.manageTrustedOrganizationsAndViewableDocuments]: {
    label: "Manage trusted organizations and viewable documents"
  },
  [OrganizationRoleCapability.createEditArchiveAnalytics]: {
    label: "Create, edit & archive all Analytic Documents"
  },
  [OrganizationRoleCapability.createEditArchiveDashboards]: {
    label: "Create, edit & archive all Dashboards"
  },
  [OrganizationRoleCapability.manageDebug]: {
    label: 'Manage Debug'
  },
  [OrganizationRoleCapability.editAllSubmissionDetails]: {
    label: 'Edit all submission details'
  },
  [OrganizationRoleCapability.deleteSubmissions]: {
    label: 'Delete submissions'
  },
  [OrganizationRoleCapability.manageUsers]: {
    label: 'Create and manage user types'
  },
  [OrganizationRoleCapability.managePeerOrganizations]: {
    label: 'Manage peer organizations'
  },
  [OrganizationRoleCapability.manageQuickLinksFolders]: {
    label: 'Manage all quick links folders'
  },
  [OrganizationRoleCapability.createQuickLinksFolders]: {
    label: 'Create quick links folders'
  },
  [OrganizationRoleCapability.createApps]: {
    label: 'Create Apps'
  },
  [OrganizationRoleCapability.downloadApps]: {
    label: 'Download Apps'
  },
  [OrganizationRoleCapability.uploadApps]: {
    label: 'Upload to Apps'
  },
  [OrganizationRoleCapability.manageDataTags]: {
    label: 'Create, edit & delete data tags'
  },
  [OrganizationRoleCapability.manageDataTagReports]: {
    label: 'View data tag reports'
  },
  [OrganizationRoleCapability.manageAppGroupsAndPermissions]: {
    label: 'Manage app groups & permissions'
  },
  [OrganizationRoleCapability.transferAppOwner]: {
    label: 'Transfer app owner'
  },
  [OrganizationRoleCapability.transferAppGroupOwner]: {
    label: 'Transfer app group owner'
  },
  [OrganizationRoleCapability.shareConnections]: {
    label: 'Share connections',
  },
};

export const OrganizationRoleData: Hash<Hash<IRoleData<OrganizationRoleCapability>[]>> = {
  'General Settings': {
    'Manage Memberships': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageMembers].label,
        capability: OrganizationRoleCapability.manageMembers
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageRoles].label,
        capability: OrganizationRoleCapability.manageRoles
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.createGroups].label,
        capability: OrganizationRoleCapability.createGroups
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageAllGroups].label,
        capability: OrganizationRoleCapability.manageAllGroups
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.viewAllMemberProfiles].label,
        capability: OrganizationRoleCapability.viewAllMemberProfiles
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageEntities].label,
        capability: OrganizationRoleCapability.manageEntities
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageUsers].label,
        capability: OrganizationRoleCapability.manageUsers
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageTrustedOrganizationsAndViewableDocuments].label,
        capability: OrganizationRoleCapability.manageTrustedOrganizationsAndViewableDocuments
      }
    ],
    'Manage Quick Links': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageQuickLinksFolders].label,
        capability: OrganizationRoleCapability.manageQuickLinksFolders
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.createQuickLinksFolders].label,
        capability: OrganizationRoleCapability.createQuickLinksFolders
      },
    ],
    'Manage Organization': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageConnections].label,
        capability: OrganizationRoleCapability.manageConnections
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.shareConnections].label,
        capability: OrganizationRoleCapability.shareConnections
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageSettings].label,
        capability: OrganizationRoleCapability.manageSettings
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageProfiles].label,
        capability: OrganizationRoleCapability.manageProfiles
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageDebug].label,
        capability: OrganizationRoleCapability.manageDebug
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageDataTags].label,
        capability: OrganizationRoleCapability.manageDataTags
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageDataTagReports].label,
        capability: OrganizationRoleCapability.manageDataTagReports
      },
    ],
    'Manage Peer Organizations': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.managePeerOrganizations].label,
        capability: OrganizationRoleCapability.managePeerOrganizations,
      }
    ]
  },
  'All Documents/Folders': {
    'Manage All Apps': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.createApps].label,
        capability: OrganizationRoleCapability.createApps
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.downloadApps].label,
        capability: OrganizationRoleCapability.downloadApps
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.uploadApps].label,
        capability: OrganizationRoleCapability.uploadApps
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageAppGroupsAndPermissions].label,
        capability: OrganizationRoleCapability.manageAppGroupsAndPermissions
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.transferAppOwner].label,
        capability: OrganizationRoleCapability.transferAppOwner
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.transferAppGroupOwner].label,
        capability: OrganizationRoleCapability.transferAppGroupOwner
      },
    ],
    'Manage All Documents & Folders': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.createDocumentsAndFolders].label,
        capability: OrganizationRoleCapability.createDocumentsAndFolders
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageObjectModels].label,
        capability: OrganizationRoleCapability.manageObjectModels
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.editAllDocumentsAndFolders].label,
        capability: OrganizationRoleCapability.editAllDocumentsAndFolders
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.moveArchiveAllDocumentsAndFolders].label,
        capability: OrganizationRoleCapability.moveArchiveAllDocumentsAndFolders
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.publishUnpublishAllOrganizationalDocuments].label,
        capability: OrganizationRoleCapability.publishUnpublishAllOrganizationalDocuments
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageAllDocumentAndFolderSharesAndPermissions].label,
        capability: OrganizationRoleCapability.manageAllDocumentAndFolderSharesAndPermissions
      }
    ],
    'Manage All Templates': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageTemplates].label,
        capability: OrganizationRoleCapability.manageTemplates
      }
    ],
    'Manage All Analytics & Dashboards': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.createEditArchiveAnalytics].label,
        capability: OrganizationRoleCapability.createEditArchiveAnalytics
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.createEditArchiveDashboards].label,
        capability: OrganizationRoleCapability.createEditArchiveDashboards
      }
    ],
  },
  'All Submissions/Licenses': {
    'Manage All Submissions': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.viewAllDocumentsOnSubmissionsDashboard].label,
        capability: OrganizationRoleCapability.viewAllDocumentsOnSubmissionsDashboard
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.viewAllSubmissionDetails].label,
        capability: OrganizationRoleCapability.viewAllSubmissionDetails
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.viewAllSubmissionDetailsAndExport].label,
        capability: OrganizationRoleCapability.viewAllSubmissionDetailsAndExport
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.editAllSubmissionDetails].label,
        capability: OrganizationRoleCapability.editAllSubmissionDetails
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.inputExternalSubmissionData].label,
        capability: OrganizationRoleCapability.inputExternalSubmissionData
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.assignGroupMembersToWorkflowSteps].label,
        capability: OrganizationRoleCapability.assignGroupMembersToWorkflowSteps
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.reassignCompletersForWorkflowSteps].label,
        capability: OrganizationRoleCapability.reassignCompletersForWorkflowSteps
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.terminateSubmissionWorkflowSteps].label,
        capability: OrganizationRoleCapability.terminateSubmissionWorkflowSteps
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.deleteSubmissions].label,
        capability: OrganizationRoleCapability.deleteSubmissions
      }
    ],
    'Manage All Licenses': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.viewAllLicenseHistories].label,
        capability: OrganizationRoleCapability.viewAllLicenseHistories
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.viewLicenseeHistory].label,
        capability: OrganizationRoleCapability.viewLicenseeHistory
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.viewComplaints].label,
        capability: OrganizationRoleCapability.viewComplaints
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageAllLicenseStatuses].label,
        capability: OrganizationRoleCapability.manageAllLicenseStatuses
      }
    ]
  },
  'All Portals': {
    'Manage All Portals': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.createPortals].label,
        capability: OrganizationRoleCapability.createPortals
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.editAllPortals].label,
        capability: OrganizationRoleCapability.editAllPortals
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.deleteAllPortals].label,
        capability: OrganizationRoleCapability.deleteAllPortals
      }
    ],
    'Manage Portal Documents': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.addRemoveDocumentsInAllPortals].label,
        capability: OrganizationRoleCapability.addRemoveDocumentsInAllPortals
      }
    ],
    'Manage Portal Sharing': [
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.publishUnpublishAllPortals].label,
        capability: OrganizationRoleCapability.publishUnpublishAllPortals
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.manageAllPortalSharesAndPermissions].label,
        capability: OrganizationRoleCapability.manageAllPortalSharesAndPermissions
      },
      {
        label: OrganizationRoleMetadata[OrganizationRoleCapability.configureAPI].label,
        capability: OrganizationRoleCapability.configureAPI
      }
    ]
  },
};

export let portalCapabilities = [
  OrganizationRoleCapability.createPortals,
  OrganizationRoleCapability.editAllPortals,
  OrganizationRoleCapability.deleteAllPortals,
  OrganizationRoleCapability.addRemoveDocumentsInAllPortals,
  OrganizationRoleCapability.publishUnpublishAllPortals,
  OrganizationRoleCapability.manageAllPortalSharesAndPermissions,
  OrganizationRoleCapability.configureAPI
];

export let viewPortalBuilderCapabilities = [
  OrganizationRoleCapability.editAllPortals,
  OrganizationRoleCapability.addRemoveDocumentsInAllPortals,
  OrganizationRoleCapability.publishUnpublishAllPortals,
  OrganizationRoleCapability.manageAllPortalSharesAndPermissions,
  OrganizationRoleCapability.configureAPI
];

export let viewPortalManagementCapabilities = portalCapabilities;

export let viewReportingDashboardCapabilities = [
  OrganizationRoleCapability.viewAllDocumentsOnSubmissionsDashboard,
  OrganizationRoleCapability.viewAllSubmissionDetailsAndExport,
  OrganizationRoleCapability.viewAllSubmissionDetails,
  OrganizationRoleCapability.inputExternalSubmissionData
];

export let viewOrganizationManagementCapabilities = [
  OrganizationRoleCapability.manageMembers,
  OrganizationRoleCapability.manageRoles,
  OrganizationRoleCapability.createGroups,
  OrganizationRoleCapability.manageAllGroups,
  OrganizationRoleCapability.viewAllMemberProfiles,
  OrganizationRoleCapability.manageEntities,
  OrganizationRoleCapability.manageTrustedOrganizationsAndViewableDocuments,
];

export let updateOrganizationCapabilities = [
  OrganizationRoleCapability.manageMembers,
  OrganizationRoleCapability.manageSettings,
  OrganizationRoleCapability.manageRoles
];

export let view_organization_settings_capabilities = [
  OrganizationRoleCapability.manageSettings,
  OrganizationRoleCapability.manageConnections,
  OrganizationRoleCapability.manageTemplates,
];
